import {DOCUMENT} from '@angular/common';
import {AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {NameGeneratorProperty} from '@domain/custom-settings/name-generation-property';
import {FontFamily} from '@domain/fonts/font-family.enum';
import {FontStyle} from '@domain/fonts/font-style.enum';
import {FontWeight} from '@domain/fonts/font-weight.enum';
import {NameGeneratorSeparator} from '@domain/name-generator/enums/name-generator-separator.enum';
import {FreeZoneSettings} from '@domain/production-schedule/custom-settings/free-zone-settings';
import {LoomScheduleSettings} from '@domain/production-schedule/custom-settings/loom-schedule-settings';
import {PathLabelSettings} from '@domain/production-schedule/custom-settings/path-label-settings';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {RestZoneSettings} from '@domain/production-schedule/custom-settings/rest-zone-settings';
import {LoomDeletePolicy} from '@domain/production-schedule/loom-delete-policy/loom-delete-policy.enum';
import {RestZonePosition} from '@domain/production-schedule/rest-zone/rest-zone-position.enum';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription as ProfileSubscription} from '@domain/profile/subscription';
import {PropertyValue} from '@domain/property-value';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {HttpProductionOrdersCustomSettingsService} from '@infrastructure/http/production-order-custom-settings/http-production-orders-custom-settings.service';
import {BackendLimitsConstants} from '@shared/constants/backend-limits.constants';
import {BaseComponent, canShowErrorForErrorCodeAndFormControlForFormGroup, EnumUtils, FormValidationHelper, ToastService, TranslateService} from '@vdw/angular-component-library';
import {some} from 'lodash-es';
import {forkJoin} from 'rxjs';
import {distinctUntilChanged, finalize, startWith, takeUntil} from 'rxjs/operators';
import {ProductionScheduleCustomSettingsForm} from './production-schedule-custom-settings-form';

@Component({
  selector: 'app-production-schedule-custom-settings',
  templateUrl: './production-schedule-custom-settings.component.html',
  styleUrls: ['./production-schedule-custom-settings.component.scss']
})
export class ProductionScheduleCustomSettingsComponent extends BaseComponent implements OnInit, AfterViewInit {
  private readonly VIEW_LOSS_PERMISSION: Permission = Permission.VIEW_LOSS;
  private readonly PRODUCTION_SCHEDULE_OVERVIEW_URL = NavigationUtils.getAbsolutePath(NavigationId.CARPET_PRODUCTION_ORDER);

  public readonly INT_32_MAXIMUM_VALUE = BackendLimitsConstants.INT32_MAX;

  public initialCustomSettings: ProductionOrderCustomSettings;
  public productionScheduleCustomSettingsForm: ProductionScheduleCustomSettingsForm;
  public nameGenerationProperties: NameGeneratorProperty[];
  public pathLabelNameGenerationProperties: NameGeneratorProperty[];

  public restZonePositionEnum = RestZonePosition;
  public possibleRestZonePositions: RestZonePosition[] = RestZonePosition.getRestZonePositions();
  public possibleUserDefinedRestZonePositions: RestZonePosition[] = RestZonePosition.getUserDefinedRestZonePositions();
  public possibleLoomDeletePolicies: LoomDeletePolicy[] = EnumUtils.getEnumValues(LoomDeletePolicy);
  public possibleFontFamilies: FontFamily[] = EnumUtils.getEnumValues(FontFamily);
  public possibleFontWeights: FontWeight[] = EnumUtils.getEnumValues(FontWeight);
  public possibleFontStyles: FontStyle[] = EnumUtils.getEnumValues(FontStyle);
  public hasPlanningBoardPermission: boolean = false;

  private alreadyAdjustedCustomSettings = false;
  private saveButtonTouched = false;
  private showCustomSettings = false;

  private currentSubscription: ProfileSubscription;

  public constructor(
    private readonly toastService: ToastService,
    private readonly httpProductionOrdersCustomSettings: HttpProductionOrdersCustomSettingsService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly navigationHelperService: NavigationHelperService<PropertyValue[]>
  ) {
    super();
  }

  public ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.hasPlanningBoardPermission =
      (this.currentSubscription?.hasPermission(Permission.TEXFAB_PLANNINGBOARD) ?? false) || (this.currentSubscription?.hasPermission(Permission.TEXFAB_PLANNINGBOARD_NEW) ?? false);
    this.initialiseCustomSettings();
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.onDestroy();
  }

  public canShowErrorForFormControl(errorCode: string, formControlName: string): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, this.productionScheduleCustomSettingsForm);
  }

  public canShowInvalidFormMessageError(): boolean {
    const isFormInvalid = some(this.productionScheduleCustomSettingsForm?.controls, (control: FormControl) => control.invalid && control.touched);
    if (!isFormInvalid) {
      this.saveButtonTouched = false;
    }
    return isFormInvalid && this.saveButtonTouched;
  }

  public saveCustomSettings(): void {
    this.saveButtonTouched = true;
    const isValid = new FormValidationHelper().checkForms([this.productionScheduleCustomSettingsForm], this.document);

    if (isValid) {
      this.saving = true;

      const formValue = this.productionScheduleCustomSettingsForm.getRawValue();
      const customSettingsToSave: ProductionOrderCustomSettings = new ProductionOrderCustomSettings(
        undefined,
        formValue.nameGenerator,
        formValue.maximumLossInPercent,
        formValue.maxDifferenceInPathCommercialLength,
        formValue.allowExceedingRequestedAmountOfOrderLineItems,
        formValue.allowPreFilterOnQualitiesAndColoredYarnSets,
        formValue.onPlanningBoard,
        new RestZoneSettings(formValue.restZones.position, formValue.restZones.userDefinedRestZonePosition, formValue.restZones.minimumWidth),
        new FreeZoneSettings(
          formValue.freeZones.leftCommercialWidth,
          formValue.freeZones.leftUseAsMaximum,
          formValue.freeZones.rightCommercialWidth,
          formValue.freeZones.rightUseAsMaximum,
          formValue.freeZones.inBetweenCommercialWidth,
          formValue.freeZones.inBetweenUseAsMaximum
        ),
        new PathLabelSettings(
          formValue.pathLabel.nameGenerator,
          this.initialCustomSettings.pathLabel.technicalHeightInPicks,
          formValue.labels.labelFontFamily,
          formValue.labels.labelFontWeight,
          formValue.labels.labelFontStyle,
          formValue.labels.commercialLabelHeightInMM
        ),
        new LoomScheduleSettings(
          formValue.loomDeletePolicy.loomDeletePolicyOption,
          formValue.loomDeletePolicy.loomDeletePolicyTimePeriodInDays,
          formValue.loomDeletePolicy.scheduleFileAutoDeleteAfterStartOnLoom,
          formValue.loomDeletePolicy.scheduleOnlyStartFromQueue
        ),
        this.initialCustomSettings.epNameGenerator
      );

      this.httpProductionOrdersCustomSettings
        .save(customSettingsToSave)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving()))
        .subscribe(() => {
          this.toastService.showInfo({
            tapToDismiss: false,
            timeOut: 2000,
            message: this.translate.instant('GENERAL.ACTIONS.SAVED_OBJECT', {
              object: this.translate.instant(this.translate.instant('GENERAL.CUSTOM_SETTINGS.CUSTOM_SETTINGS').toLowerCase())
            })
          });

          this.navigateBack();
        });
    }
  }

  public canShowCancelButton(): boolean {
    return this.alreadyAdjustedCustomSettings;
  }

  public navigateBack(): void {
    this.navigationHelperService.navigateToPreviousRoute(this.PRODUCTION_SCHEDULE_OVERVIEW_URL);
  }

  public canShowCustomSettings(): boolean {
    return this.showCustomSettings;
  }

  public hasViewLossPermission(): boolean {
    return this.currentSubscription?.hasPermission(this.VIEW_LOSS_PERMISSION);
  }

  public getKeysOfNameGeneratorSeparator(): string[] {
    return EnumUtils.getEnumNames(NameGeneratorSeparator);
  }

  private setFormFields(): void {
    this.productionScheduleCustomSettingsForm = this.formBuilder.group({
      nameGenerator: this.formBuilder.control(this.initialCustomSettings.nameGenerator),
      maximumLossInPercent: this.formBuilder.control({value: this.initialCustomSettings.maximumLossInPercent, disabled: !this.hasViewLossPermission()}, Validators.required),
      maxDifferenceInPathCommercialLength: this.formBuilder.control(this.initialCustomSettings.maxDifferenceInPathLengthInMillimeters, Validators.required),
      allowExceedingRequestedAmountOfOrderLineItems: this.formBuilder.control(this.initialCustomSettings.allowExceedingRequestedAmountOfOrderLineItems),
      onPlanningBoard: this.formBuilder.control(this.initialCustomSettings.addProductionOrderToPlanningBoard),
      allowPreFilterOnQualitiesAndColoredYarnSets: this.formBuilder.control(this.initialCustomSettings.allowPreFilterOnQualitiesAndColoredYarnSets),
      restZones: this.formBuilder.group({
        position: this.formBuilder.control(this.initialCustomSettings.restZone.restPosition),
        userDefinedRestZonePosition: this.formBuilder.control(this.initialCustomSettings.restZone.userDefinedRestPosition),
        minimumWidth: this.formBuilder.control(this.initialCustomSettings.restZone.maxDentsRestToDivideOverFree, [Validators.required, Validators.max(this.INT_32_MAXIMUM_VALUE), Validators.min(0)])
      }),
      freeZones: this.formBuilder.group({
        leftCommercialWidth: this.formBuilder.control(this.initialCustomSettings.freeZone.leftWidthInMillimeters, Validators.required),
        leftUseAsMaximum: this.formBuilder.control(this.initialCustomSettings.freeZone.leftUseAsMaximum),
        rightCommercialWidth: this.formBuilder.control(this.initialCustomSettings.freeZone.rightWidthInMillimeters, Validators.required),
        rightUseAsMaximum: this.formBuilder.control(this.initialCustomSettings.freeZone.rightUseAsMaximum),
        inBetweenCommercialWidth: this.formBuilder.control(this.initialCustomSettings.freeZone.inBetweenWidthInMillimeters, Validators.required),
        inBetweenUseAsMaximum: this.formBuilder.control(this.initialCustomSettings.freeZone.inBetweenUseAsMaximum)
      }),
      labels: this.formBuilder.group({
        commercialLabelHeightInMM: this.formBuilder.control(this.initialCustomSettings.pathLabel.heightInMM, Validators.required),
        labelFontFamily: this.formBuilder.control(this.initialCustomSettings.pathLabel.fontFamily),
        labelFontWeight: this.formBuilder.control(this.initialCustomSettings.pathLabel.fontWeight),
        labelFontStyle: this.formBuilder.control(this.initialCustomSettings.pathLabel.fontStyle)
      }),
      pathLabel: this.formBuilder.group({
        nameGenerator: this.formBuilder.control(this.initialCustomSettings.pathLabel.nameGenerator)
      }),
      loomDeletePolicy: this.formBuilder.group({
        loomDeletePolicyOption: this.formBuilder.control(this.initialCustomSettings.loomSchedule.deletePolicy),
        loomDeletePolicyTimePeriodInDays: this.formBuilder.control(this.initialCustomSettings.loomSchedule.deletePolicyTimePeriodInDays, [Validators.required, Validators.min(1)]),
        scheduleFileAutoDeleteAfterStartOnLoom: this.formBuilder.control(this.initialCustomSettings.loomSchedule.scheduleFileAutoDeleteAfterStartOnLoom),
        scheduleOnlyStartFromQueue: this.formBuilder.control(this.initialCustomSettings.loomSchedule.scheduleOnlyStartFromQueue)
      })
    });

    this.setValueWatcherForRestZonePosition();
    this.setValueWatcherForLoomDeletePolicy();
  }

  private setValueWatcherForRestZonePosition(): void {
    this.productionScheduleCustomSettingsForm.controls.restZones.controls.position.valueChanges
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), distinctUntilChanged(), startWith(this.productionScheduleCustomSettingsForm.controls.restZones.controls.position.value))
      .subscribe((restZonePosition: RestZonePosition) => {
        if (restZonePosition === RestZonePosition.USER_DEFINED) {
          this.productionScheduleCustomSettingsForm.controls.restZones.controls.userDefinedRestZonePosition.enable();
          this.productionScheduleCustomSettingsForm.controls.restZones.controls.userDefinedRestZonePosition.setValue(
            this.initialCustomSettings.restZone.userDefinedRestPosition ?? RestZonePosition.LEFT
          );
        } else {
          this.productionScheduleCustomSettingsForm.controls.restZones.controls.userDefinedRestZonePosition.disable();
          this.productionScheduleCustomSettingsForm.controls.restZones.controls.userDefinedRestZonePosition.setValue(null);
        }
      });
  }

  private setValueWatcherForLoomDeletePolicy(): void {
    this.productionScheduleCustomSettingsForm.controls.loomDeletePolicy.controls.loomDeletePolicyOption.valueChanges
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), distinctUntilChanged(), startWith(this.productionScheduleCustomSettingsForm.controls.loomDeletePolicy.controls.loomDeletePolicyOption.value))
      .subscribe((loomDeletePolicy: LoomDeletePolicy) => {
        if (loomDeletePolicy === LoomDeletePolicy.TIME_PERIOD) {
          this.productionScheduleCustomSettingsForm.controls.loomDeletePolicy.controls.loomDeletePolicyTimePeriodInDays.enable();
          this.productionScheduleCustomSettingsForm.controls.loomDeletePolicy.controls.loomDeletePolicyTimePeriodInDays.setValue(
            this.initialCustomSettings.loomSchedule.deletePolicyTimePeriodInDays ?? 1
          );
        } else {
          this.productionScheduleCustomSettingsForm.controls.loomDeletePolicy.controls.loomDeletePolicyTimePeriodInDays.disable();
          this.productionScheduleCustomSettingsForm.controls.loomDeletePolicy.controls.loomDeletePolicyTimePeriodInDays.setValue(null);
        }
      });
  }

  private initialiseCustomSettings(): void {
    forkJoin([
      this.httpProductionOrdersCustomSettings.get(),
      this.httpProductionOrdersCustomSettings.hasAlreadyAdjusted(),
      this.httpProductionOrdersCustomSettings.getNameGenerationProperties(),
      this.httpProductionOrdersCustomSettings.getPathLabelNameGenerationProperties()
    ])
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(
        ([customSettings, alreadyAdjustedCustomSettings, nameGenerationProperties, pathLabelNameGenerationProperties]: [
          ProductionOrderCustomSettings,
          boolean,
          NameGeneratorProperty[],
          NameGeneratorProperty[]
        ]) => {
          this.initialCustomSettings = customSettings;
          this.alreadyAdjustedCustomSettings = alreadyAdjustedCustomSettings;
          this.nameGenerationProperties = nameGenerationProperties;
          this.pathLabelNameGenerationProperties = pathLabelNameGenerationProperties;

          this.showCustomSettings = true;

          this.setFormFields();
        }
      );
  }
}
